import { useState, useEffect, useRef, useCallback, memo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TitleIcon from '@mui/icons-material/Title';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DOMPurify from 'dompurify';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useBuilderTranslation } from '@/i18n/hooks/useTypedTranslation';
import { TRUE_FALSE_PRESETS } from '@/constants';
import { OtherOptionToggle, SelectionLimitsEditor, QuestionTypeMenu, RichTextEditor, type OtherTextMode } from './';
import { ConditionBuilder } from '../features/ConditionBuilder';
import type { Question, QuestionOption, QuestionType, QuestionCategory, QuestionDisplayCondition } from '@/types';

// Question type icons and colors - Academic palette
const typeConfig: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  SECTION: { icon: TitleIcon, color: '#2BC4AD', label: 'Section' },
  OPEN_TEXT: { icon: TextFieldsIcon, color: '#1F2A44', label: 'Short answer' },
  SINGLE_CHOICE: { icon: RadioButtonCheckedIcon, color: '#4B5563', label: 'Multiple choice' },
  MULTIPLE_CHOICE: { icon: CheckBoxIcon, color: '#2BC4AD', label: 'Checkboxes' },
  RANKED_CHOICE: { icon: FormatListNumberedIcon, color: '#1F2A44', label: 'Ranking' },
  SCALE5: { icon: LinearScaleIcon, color: '#4B5563', label: 'Linear scale' },
  TRUE_FALSE: { icon: ToggleOnIcon, color: '#9CA3AF', label: 'Yes/No' },
};

interface InlineQuestionEditorProps {
  question: Question;
  questionNumber?: string; // Kept for backwards compatibility but no longer displayed
  questionTypes: QuestionType[];
  allQuestions: Question[];
  categories?: QuestionCategory[];
  isNew?: boolean;
  isFocused?: boolean;
  /** True when any question in the list is being dragged */
  isDraggingAny?: boolean;
  saving?: boolean;
  onUpdate: (questionId: number, updates: Partial<Question> & { options?: QuestionOption[]; displayConditions?: QuestionDisplayCondition[] }) => void;
  onDelete: (questionId: number) => void;
  onDuplicate: (question: Question) => void;
  onFocus: (questionId: number) => void;
  onCancel?: () => void;
}

// Memoized component to prevent unnecessary re-renders
export const InlineQuestionEditor = memo(function InlineQuestionEditor({
  question,
  questionTypes,
  allQuestions,
  categories = [],
  isNew = false,
  isFocused = false,
  isDraggingAny = false,
  saving = false,
  onUpdate,
  onDelete,
  onDuplicate,
  onFocus,
  onCancel,
}: InlineQuestionEditorProps) {
  const { t } = useBuilderTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const lastQuestionIdRef = useRef<number>(question.id);
  const wasFocusedRef = useRef(false);
  // Track newly added option for auto-focus (using state since it affects render)
  const [newOptionIndex, setNewOptionIndex] = useState<number | null>(null);
  // Debounce timer for batching rapid updates
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingUpdateRef = useRef<Partial<Question> & { options?: QuestionOption[] } | null>(null);

  // Local state for inline editing - initialized from question props
  // useState only uses initial value on first render, useEffect syncs on question.id change
  const [localText, setLocalText] = useState(question.text || '');
  // Filter out "Other" options - they are managed by the backend via AllowOtherText flag
  const filterRegularOptions = (opts: QuestionOption[] | undefined) => (opts || []).filter(o => !o.isOtherOption);
  const [localOptions, setLocalOptions] = useState<QuestionOption[]>(filterRegularOptions(question.options));
  const [showTypeSettings, setShowTypeSettings] = useState(false);

  // Type-specific local state
  const [localAllowOther, setLocalAllowOther] = useState(question.allowOtherText || false);
  const [localOtherMode, setLocalOtherMode] = useState<OtherTextMode>((question.metadata?.otherTextMode as OtherTextMode) || 'short');
  const [localMinSelections, setLocalMinSelections] = useState<number | undefined>(question.minSelections);
  const [localMaxSelections, setLocalMaxSelections] = useState<number | undefined>(question.maxSelections);

  // Open Text settings
  const [localPlaceholder, setLocalPlaceholder] = useState<string>((question.metadata?.placeholder as string) || '');
  const [localMaxLength, setLocalMaxLength] = useState<number | undefined>(question.metadata?.maxLength as number | undefined);

  // Scale settings
  const [localStartLabel, setLocalStartLabel] = useState<string>((question.metadata?.startLabel as string) || '');
  const [localEndLabel, setLocalEndLabel] = useState<string>((question.metadata?.endLabel as string) || '');

  // True/False settings - prioritize labels from options (for conditional display), then metadata, then defaults
  const initTrueLabel = (): string => {
    const trueOption = question.options?.find(o => o.numericValue === 1);
    return trueOption?.label || (question.metadata?.trueLabel as string) || 'Yes';
  };
  const initFalseLabel = (): string => {
    const falseOption = question.options?.find(o => o.numericValue === 0);
    return falseOption?.label || (question.metadata?.falseLabel as string) || 'No';
  };
  const [localTrueLabel, setLocalTrueLabel] = useState<string>(initTrueLabel);
  const [localFalseLabel, setLocalFalseLabel] = useState<string>(initFalseLabel);

  // Section settings (description stored in helpText)
  const [localDescription, setLocalDescription] = useState<string>(question.helpText || '');

  // Conditions - keep locally to allow incomplete conditions in UI
  const [localConditions, setLocalConditions] = useState<QuestionDisplayCondition[]>(
    question.displayConditions || []
  );

  const typeCode = question.questionTypeCode || question.questionType?.code || 'OPEN_TEXT';
  const config = typeConfig[typeCode] || typeConfig.OPEN_TEXT;
  const TypeIcon = config.icon;

  // Sortable hook for drag-and-drop (prefixed ID to distinguish from sections)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `question-${question.id}` });

  // Enhanced drag styles for Google Forms-like experience
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    // Smooth transition for items shifting during drag
    transition: isDragging
      ? undefined // No transition on dragged item (handled by DragOverlay)
      : isDraggingAny
        ? 'transform 200ms cubic-bezier(0.2, 0, 0, 1)'
        : transition,
    // When being dragged, show as a subtle placeholder
    opacity: isDragging ? 0.4 : 1,
    // Add a visual "hole" effect when item is being dragged
    ...(isDragging && {
      boxShadow: 'inset 0 0 0 2px rgba(43, 196, 173, 0.3)',
      background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(43, 196, 173, 0.03) 10px, rgba(43, 196, 173, 0.03) 20px)',
    }),
  };

  // Focus text input when new question is created
  useEffect(() => {
    if (isNew && textInputRef.current) {
      textInputRef.current.focus();
    }
  }, [isNew]);

  // Scroll into view when question becomes focused (fixes viewport jump issue)
  useEffect(() => {
    if (isFocused && !wasFocusedRef.current && cardRef.current) {
      // Wait for collapse/expand animations to settle before checking position
      const timeoutId = setTimeout(() => {
        if (!cardRef.current) return;

        const rect = cardRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const headerOffset = 80; // Account for fixed header if any

        // Check if element is significantly out of view
        const isAboveViewport = rect.top < headerOffset;
        const isBelowViewport = rect.bottom > viewportHeight - 20;

        if (isAboveViewport || isBelowViewport) {
          // Scroll to position the card with some padding from top
          const scrollTop = window.scrollY + rect.top - headerOffset - 20;
          window.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: 'smooth',
          });
        }
      }, 150); // Wait for animations to complete

      return () => clearTimeout(timeoutId);
    }
    wasFocusedRef.current = isFocused;
  }, [isFocused]);

  // Sync local state only when question ID changes (not on every update)
  useEffect(() => {
    if (lastQuestionIdRef.current !== question.id) {
      lastQuestionIdRef.current = question.id;
      setLocalText(question.text || '');
      setLocalOptions(filterRegularOptions(question.options));
      setLocalAllowOther(question.allowOtherText || false);
      setLocalOtherMode((question.metadata?.otherTextMode as OtherTextMode) || 'short');
      setLocalMinSelections(question.minSelections);
      setLocalMaxSelections(question.maxSelections);
      setLocalPlaceholder((question.metadata?.placeholder as string) || '');
      setLocalMaxLength(question.metadata?.maxLength as number | undefined);
      setLocalStartLabel((question.metadata?.startLabel as string) || '');
      setLocalEndLabel((question.metadata?.endLabel as string) || '');
      // For TRUE_FALSE, prioritize labels from options (for conditional display), then metadata, then defaults
      const trueOption = question.options?.find(o => o.numericValue === 1);
      const falseOption = question.options?.find(o => o.numericValue === 0);
      setLocalTrueLabel(trueOption?.label || (question.metadata?.trueLabel as string) || 'Yes');
      setLocalFalseLabel(falseOption?.label || (question.metadata?.falseLabel as string) || 'No');
      setLocalDescription(question.helpText || '');
      setLocalConditions(question.displayConditions || []);
      setShowTypeSettings(false);
    }
  }, [question.id]);

  // Sync options when API returns new IDs (after creating options)
  // This handles the case where local options have negative IDs that need to be replaced
  // Use ref to track local options without triggering effect on every change
  const localOptionsRef = useRef(localOptions);

  // Update ref in effect, not during render (React Compiler requirement)
  useEffect(() => {
    localOptionsRef.current = localOptions;
  }, [localOptions]);

  // Sync options when server returns new IDs for temporary options OR when options are deleted
  useEffect(() => {
    const currentLocal = localOptionsRef.current;
    // Compare only regular options (exclude "Other" which is managed by backend)
    const serverRegularOptions = filterRegularOptions(question.options);
    const hasNegativeLocalIds = currentLocal.some(opt => opt.id < 0);
    const hasPositiveServerIds = serverRegularOptions.every(opt => opt.id > 0);
    const sameLength = serverRegularOptions.length === currentLocal.length;
    const serverHasMore = serverRegularOptions.length > currentLocal.length;
    const serverHasFewer = serverRegularOptions.length < currentLocal.length;

    // If we have negative local IDs and server returned all positive IDs with same length, sync
    // This handles the case where backend assigned real IDs to temporary options
    if (hasNegativeLocalIds && hasPositiveServerIds && sameLength && serverRegularOptions.length > 0) {
      setLocalOptions(serverRegularOptions);
    }

    // Only sync when server has MORE options (backend confirmed addition)
    // OR when server has FEWER options AND we don't have unsaved negative IDs
    // This prevents losing locally-added options that haven't been saved yet
    if (serverHasMore && serverRegularOptions.length > 0) {
      setLocalOptions(serverRegularOptions);
    } else if (serverHasFewer && !hasNegativeLocalIds && serverRegularOptions.length > 0) {
      // Server has fewer AND we don't have pending additions, so it's a confirmed deletion
      setLocalOptions(serverRegularOptions);
    }
  }, [question.options]);

  // Build update payload with all current settings
  const buildUpdatePayload = useCallback(() => {
    const metadata: Record<string, unknown> = { ...question.metadata };

    // Add type-specific metadata
    if (typeCode === 'OPEN_TEXT') {
      metadata.placeholder = localPlaceholder;
      metadata.maxLength = localMaxLength;
    } else if (typeCode === 'SCALE5') {
      metadata.startLabel = localStartLabel;
      metadata.endLabel = localEndLabel;
    } else if (typeCode === 'TRUE_FALSE') {
      metadata.trueLabel = localTrueLabel;
      metadata.falseLabel = localFalseLabel;
    }

    if (['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'RANKED_CHOICE'].includes(typeCode)) {
      metadata.otherTextMode = localOtherMode;
    }

    // Build options for TRUE_FALSE type to enable conditional display
    // This syncs the labels to actual QuestionOption objects that ConditionBuilder can use
    let trueFalseOptions: QuestionOption[] | undefined;
    if (typeCode === 'TRUE_FALSE') {
      const existingOptions = question.options || [];
      const existingTrueOption = existingOptions.find(o => o.numericValue === 1);
      const existingFalseOption = existingOptions.find(o => o.numericValue === 0);

      trueFalseOptions = [
        {
          id: existingTrueOption?.id || 0,
          questionId: question.id,
          label: localTrueLabel || 'Yes',
          code: 'T',
          numericValue: 1,
          displayOrder: 1,
          isOtherOption: false,
          isActive: true,
          isDeleted: false,
          createdDate: existingTrueOption?.createdDate || new Date().toISOString(),
        },
        {
          id: existingFalseOption?.id || 0,
          questionId: question.id,
          label: localFalseLabel || 'No',
          code: 'F',
          numericValue: 0,
          displayOrder: 2,
          isOtherOption: false,
          isActive: true,
          isDeleted: false,
          createdDate: existingFalseOption?.createdDate || new Date().toISOString(),
        },
      ];
    }

    return {
      text: localText,
      questionTypeId: question.questionTypeId,
      isRequired: question.isRequired,
      allowOtherText: localAllowOther,
      minSelections: localMinSelections,
      maxSelections: localMaxSelections,
      metadata, // Will be automatically serialized by request interceptor
      ...(trueFalseOptions && { options: trueFalseOptions }),
    };
  }, [
    question.metadata, question.questionTypeId, question.isRequired, question.id, question.options, typeCode,
    localText, localAllowOther, localOtherMode, localMinSelections, localMaxSelections,
    localPlaceholder, localMaxLength, localStartLabel, localEndLabel, localTrueLabel, localFalseLabel,
  ]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Debounced update function - batches rapid changes
  const debouncedUpdate = useCallback((updates: Partial<Question> & { options?: QuestionOption[] }) => {
    // Merge with any pending updates
    pendingUpdateRef.current = { ...pendingUpdateRef.current, ...updates };

    // Clear existing timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Schedule the actual update after a short delay
    updateTimeoutRef.current = setTimeout(() => {
      if (pendingUpdateRef.current) {
        onUpdate(question.id, pendingUpdateRef.current);
        pendingUpdateRef.current = null;
      }
    }, 150); // 150ms debounce
  }, [question.id, onUpdate]);

  // Debounced save for text changes
  const handleTextChange = useCallback((newText: string) => {
    setLocalText(newText);
  }, []);

  const handleTextBlur = useCallback(() => {
    if (localText !== question.text) {
      onUpdate(question.id, buildUpdatePayload());
    }
  }, [localText, question.text, question.id, buildUpdatePayload, onUpdate]);

  // Section description handler
  const handleDescriptionBlur = useCallback(() => {
    if (localDescription !== question.helpText) {
      onUpdate(question.id, { ...buildUpdatePayload(), helpText: localDescription });
    }
  }, [localDescription, question.helpText, question.id, buildUpdatePayload, onUpdate]);

  // Handle option changes
  const handleOptionChange = useCallback((index: number, value: string) => {
    const newOptions = [...localOptions];
    newOptions[index] = { ...newOptions[index], label: value };
    setLocalOptions(newOptions);
  }, [localOptions]);

  const handleOptionBlur = useCallback(() => {
    // Helper to generate code from label
    const generateCode = (label: string, index: number): string => {
      if (!label.trim()) return `option_${index + 1}`;
      return label
        .toLowerCase()
        .replace(/[^a-z0-9\s_-]/g, '') // Remove special chars
        .replace(/\s+/g, '_')          // Replace spaces with underscores
        .substring(0, 50)              // Limit length
        || `option_${index + 1}`;      // Fallback if empty after cleaning
    };

    // Filter out empty options and ensure codes exist
    const validOptions = localOptions
      .filter(opt => opt.label.trim() !== '')
      .map((opt, index) => ({
        ...opt,
        code: opt.code?.trim() || generateCode(opt.label, index),
      }));
    // Quick comparison: check length first, then compare labels only
    // Compare against regular options only (exclude "Other" managed by backend)
    const serverRegularOpts = filterRegularOptions(question.options);
    const hasChanged = validOptions.length !== serverRegularOpts.length ||
      validOptions.some((opt, i) => opt.label !== serverRegularOpts[i]?.label);
    if (hasChanged) {
      onUpdate(question.id, { ...buildUpdatePayload(), options: validOptions });
    }
  }, [localOptions, question.options, question.id, buildUpdatePayload, onUpdate]);

  const handleAddOption = useCallback(() => {
    const newIndex = localOptions.length;
    const newOption: QuestionOption = {
      id: -(newIndex + 1), // Temporary negative ID
      questionId: question.id,
      label: '',
      code: '',
      displayOrder: newIndex + 1,
      isOtherOption: false,
      isActive: true,
      isDeleted: false,
      createdDate: new Date().toISOString(),
    };
    // Mark this index for auto-focus
    setNewOptionIndex(newIndex);
    setLocalOptions([...localOptions, newOption]);
  }, [localOptions, question.id]);

  const handleRemoveOption = useCallback((index: number) => {
    // Helper to generate code from label
    const generateCode = (label: string, idx: number): string => {
      if (!label.trim()) return `option_${idx + 1}`;
      return label
        .toLowerCase()
        .replace(/[^a-z0-9\s_-]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 50)
        || `option_${idx + 1}`;
    };

    const newOptions = localOptions
      .filter((_, i) => i !== index)
      .filter(opt => opt.label.trim() !== '')
      .map((opt, idx) => ({
        ...opt,
        code: opt.code?.trim() || generateCode(opt.label, idx),
      }));
    // Don't update local state here - let it sync from props after successful backend update
    // This prevents the option from disappearing if the backend rejects the deletion
    onUpdate(question.id, { ...buildUpdatePayload(), options: newOptions });
  }, [localOptions, question.id, buildUpdatePayload, onUpdate]);

  // Handle type change
  const handleTypeChange = useCallback((newTypeCode: string) => {
    const newType = questionTypes.find(t => t.code === newTypeCode);
    if (newType) {
      const updates: Partial<Question> & { options?: QuestionOption[] } = {
        questionTypeId: newType.id,
        questionTypeCode: newType.code,
        text: localText,
      };

      // Add default options for choice types
      if (newType.supportOptions && localOptions.length === 0) {
        const defaultOptions: QuestionOption[] = [
          { id: -1, questionId: question.id, label: 'Option 1', code: '', displayOrder: 1, isOtherOption: false, isActive: true, isDeleted: false, createdDate: new Date().toISOString() },
          { id: -2, questionId: question.id, label: 'Option 2', code: '', displayOrder: 2, isOtherOption: false, isActive: true, isDeleted: false, createdDate: new Date().toISOString() },
        ];
        updates.options = defaultOptions;
        setLocalOptions(defaultOptions);
      }

      onUpdate(question.id, updates);
    }
  }, [questionTypes, question.id, localOptions.length, localText, onUpdate]);

  // Handle required toggle - debounced
  const handleRequiredToggle = useCallback(() => {
    debouncedUpdate({ ...buildUpdatePayload(), isRequired: !question.isRequired });
  }, [question.isRequired, buildUpdatePayload, debouncedUpdate]);

  // Handle type-specific settings changes - debounced
  const handleAllowOtherChange = useCallback((value: boolean) => {
    setLocalAllowOther(value);
    debouncedUpdate({ ...buildUpdatePayload(), allowOtherText: value });
  }, [buildUpdatePayload, debouncedUpdate]);

  const handleOtherModeChange = useCallback((mode: OtherTextMode) => {
    setLocalOtherMode(mode);
    const payload = buildUpdatePayload();
    payload.metadata = { ...payload.metadata, otherTextMode: mode };
    debouncedUpdate(payload);
  }, [buildUpdatePayload, debouncedUpdate]);

  const handleMinSelectionsChange = useCallback((value: number | undefined) => {
    setLocalMinSelections(value);
    debouncedUpdate({ ...buildUpdatePayload(), minSelections: value });
  }, [buildUpdatePayload, debouncedUpdate]);

  const handleMaxSelectionsChange = useCallback((value: number | undefined) => {
    setLocalMaxSelections(value);
    debouncedUpdate({ ...buildUpdatePayload(), maxSelections: value });
  }, [buildUpdatePayload, debouncedUpdate]);

  // Open Text settings handlers - save immediately on blur
  const handlePlaceholderBlur = useCallback(() => {
    const payload = buildUpdatePayload();
    payload.metadata = { ...payload.metadata, placeholder: localPlaceholder };
    onUpdate(question.id, payload);
  }, [localPlaceholder, question.id, buildUpdatePayload, onUpdate]);

  const handleMaxLengthBlur = useCallback(() => {
    const payload = buildUpdatePayload();
    payload.metadata = { ...payload.metadata, maxLength: localMaxLength };
    onUpdate(question.id, payload);
  }, [localMaxLength, question.id, buildUpdatePayload, onUpdate]);

  // Scale settings handlers - debounced
  const handleScaleLabelsBlur = useCallback(() => {
    const payload = buildUpdatePayload();
    payload.metadata = { ...payload.metadata, startLabel: localStartLabel, endLabel: localEndLabel };
    debouncedUpdate(payload);
  }, [localStartLabel, localEndLabel, buildUpdatePayload, debouncedUpdate]);

  // True/False settings handlers - debounced
  const handleTrueFalseLabelsChange = useCallback((trueLabel: string, falseLabel: string) => {
    setLocalTrueLabel(trueLabel);
    setLocalFalseLabel(falseLabel);
    const payload = buildUpdatePayload();
    payload.metadata = { ...payload.metadata, trueLabel, falseLabel };
    debouncedUpdate(payload);
  }, [buildUpdatePayload, debouncedUpdate]);

  const handleTrueFalseLabelsBlur = useCallback(() => {
    const payload = buildUpdatePayload();
    payload.metadata = { ...payload.metadata, trueLabel: localTrueLabel, falseLabel: localFalseLabel };
    debouncedUpdate(payload);
  }, [localTrueLabel, localFalseLabel, buildUpdatePayload, debouncedUpdate]);

  // Handle condition changes - keep all in local state, only save complete ones - debounced
  const handleConditionsChange = useCallback((conditions: QuestionDisplayCondition[]) => {
    // Update local state with all conditions (including incomplete)
    setLocalConditions(conditions);

    // Only save conditions that have a valid requiredOptionId (complete conditions)
    // Incomplete conditions (requiredOptionId === 0) are kept in UI but not saved
    const completeConditions = conditions.filter(c => c.requiredOptionId > 0);
    debouncedUpdate({ ...buildUpdatePayload(), displayConditions: completeConditions });
  }, [buildUpdatePayload, debouncedUpdate]);

  // Check if this is a section (structural element, not an answerable question)
  const isSection = typeCode === 'SECTION';

  const supportsOptions = ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'RANKED_CHOICE'].includes(typeCode);
  const supportsOther = ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'RANKED_CHOICE'].includes(typeCode);
  const supportsLimits = ['MULTIPLE_CHOICE', 'RANKED_CHOICE'].includes(typeCode);

  // Check if type has settings (sections don't have type settings)
  const hasTypeSettings = !isSection && (typeCode === 'OPEN_TEXT' || typeCode === 'SCALE5' || typeCode === 'TRUE_FALSE' || supportsOther || supportsLimits);

  // Check if question has complete conditions (for badge display)
  const hasConditions = localConditions.filter(c => c.requiredOptionId > 0).length > 0;

  return (
    <Card
      ref={(node) => {
        // Combine refs for both sortable and scroll
        setNodeRef(node);
        (cardRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }}
      style={style}
      data-question-id={question.id}
      onClick={() => !isFocused && onFocus(question.id)}
      sx={{
        position: 'relative',
        overflow: 'visible',
        cursor: isFocused ? 'default' : 'pointer',
        borderLeft: 4,
        borderLeftColor: isFocused ? 'primary.main' : config.color,
        // Section styling: subtle background
        ...(isSection && {
          bgcolor: alpha(config.color, 0.03),
          borderLeftWidth: 6,
        }),
        // Only animate border-color and box-shadow, not layout-affecting properties
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        boxShadow: isFocused ? 4 : 1,
        '&:hover': !isFocused ? {
          boxShadow: 2,
          borderLeftColor: 'primary.main',
        } : undefined,
        // Show drag handle on hover
        '&:hover .drag-handle-top': {
          opacity: 1,
        },
      }}
    >
      {/* Top-center drag handle - Google Forms style */}
      <Box
        {...attributes}
        {...listeners}
        className="drag-handle-top"
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 48,
          height: 20,
          cursor: 'grab',
          opacity: isFocused ? 0.6 : 0,
          transition: 'opacity 0.15s ease, background-color 0.15s ease',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          bgcolor: 'transparent',
          '&:hover': {
            opacity: 1,
            bgcolor: alpha('#2BC4AD', 0.1),
          },
          '&:active': {
            cursor: 'grabbing',
            bgcolor: alpha('#2BC4AD', 0.2),
          },
          zIndex: 10,
        }}
      >
        <DragIndicatorIcon
          sx={{
            fontSize: 18,
            color: 'text.secondary',
            transform: 'rotate(90deg)',
          }}
        />
      </Box>

      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, pt: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        {/* Header row: Question number, Type selector, Actions */}
        <Box sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 1 },
          mb: { xs: 1.5, sm: 2 },
        }}>
          {/* Top row on mobile: Icon + Type selector/badge */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.75, sm: 1 },
            width: { xs: '100%', sm: 'auto' },
          }}>
            {/* Question Number or Section Icon */}
            <Box
              sx={{
                minWidth: { xs: 24, sm: 28 },
                height: { xs: 24, sm: 28 },
                borderRadius: 1.5,
                bgcolor: alpha(config.color, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <TypeIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: config.color }} />
            </Box>

            {/* Type Selector (Google Forms style dropdown) */}
            {/* SECTION type is treated as unique - cannot change to other types */}
            {isFocused && typeCode !== 'SECTION' && (
              <QuestionTypeMenu
                value={typeCode}
                questionTypes={questionTypes}
                onChange={handleTypeChange}
              />
            )}

            {/* Section badge for SECTION type (cannot be changed) */}
            {isFocused && typeCode === 'SECTION' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.5, sm: 0.75 },
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.375, sm: 0.5 },
                  bgcolor: alpha(config.color, 0.1),
                  borderRadius: 1,
                  border: 1,
                  borderColor: alpha(config.color, 0.3),
                }}
              >
                <TypeIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: config.color }} />
                <Typography variant="body2" sx={{ fontWeight: 500, color: config.color, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                  {t('questionTypes.SECTION.name')}
                </Typography>
              </Box>
            )}

            {/* Type badge when not focused */}
            {!isFocused && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: config.color, fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                  {t(`questionTypes.${typeCode}.name`)}
                </Typography>
              </Box>
            )}

            {/* Badges on mobile (when not focused) - show in same row as type */}
            {!isFocused && (
              <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 0.75, ml: 'auto' }}>
                {hasConditions && (
                  <Tooltip title={t('conditions.hasConditions')}>
                    <Chip
                      icon={<AccountTreeIcon sx={{ fontSize: 12 }} />}
                      label={localConditions.filter(c => c.requiredOptionId > 0).length}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        bgcolor: alpha('#2BC4AD', 0.1),
                        '& .MuiChip-icon': { color: 'primary.main' },
                      }}
                    />
                  </Tooltip>
                )}
                {question.isRequired && !isSection && (
                  <Typography variant="caption" color="error" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                    *
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Spacer for desktop */}
          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />

          {/* Actions - separate row on mobile */}
          {isFocused && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 0.5 },
              flexWrap: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              justifyContent: 'flex-end',
            }}>
              {/* Loading indicator - always reserve space to prevent layout shift */}
              <Box
                sx={{
                  width: { xs: 20, sm: 18 },
                  height: { xs: 20, sm: 18 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  visibility: saving ? 'visible' : 'hidden',
                  mr: { xs: 0.25, sm: 0.5 },
                }}
              >
                <CircularProgress size={18} />
              </Box>

              {/* Action buttons group */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.5 } }}>
                <Tooltip title={t('question.actions.duplicate')}>
                  <IconButton
                    size="small"
                    onClick={() => onDuplicate(question)}
                    disabled={saving}
                    sx={{
                      width: { xs: 40, sm: 40 },
                      height: { xs: 40, sm: 40 },
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title={t('question.actions.delete')}>
                  <IconButton
                    size="small"
                    onClick={() => onDelete(question.id)}
                    color="error"
                    disabled={saving}
                    sx={{
                      width: { xs: 40, sm: 40 },
                      height: { xs: 40, sm: 40 },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Required toggle (not for sections) */}
              {!isSection && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: { xs: 0.25, sm: 0.5 }, height: 24, display: { xs: 'none', sm: 'block' } }} />
                  <Tooltip title={question.isRequired ? t('question.required') : 'Optional'}>
                    <Chip
                      label={question.isRequired ? t('question.required') : 'Optional'}
                      size="small"
                      onClick={handleRequiredToggle}
                      disabled={saving}
                      icon={question.isRequired ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                      sx={{
                        ml: { xs: 0.5, sm: 0 },
                        height: { xs: 28, sm: 26 },
                        fontSize: { xs: '0.75rem', sm: '0.75rem' },
                        fontWeight: 500,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        bgcolor: question.isRequired
                          ? alpha('#2BC4AD', 0.15)
                          : alpha('#000', 0.04),
                        color: question.isRequired
                          ? '#2BC4AD'
                          : 'text.secondary',
                        border: 1,
                        borderColor: question.isRequired
                          ? alpha('#2BC4AD', 0.3)
                          : 'divider',
                        '&:hover': !saving ? {
                          bgcolor: question.isRequired
                            ? alpha('#2BC4AD', 0.25)
                            : alpha('#000', 0.08),
                          borderColor: question.isRequired
                            ? alpha('#2BC4AD', 0.5)
                            : alpha('#000', 0.2),
                          transform: 'scale(1.02)',
                        } : undefined,
                        '&:active': !saving ? {
                          transform: 'scale(0.98)',
                        } : undefined,
                        '& .MuiChip-icon': {
                          fontSize: { xs: 16, sm: 16 },
                          color: question.isRequired
                            ? '#2BC4AD'
                            : 'text.disabled',
                          ml: 0.5,
                        },
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 1 },
                        },
                      }}
                    />
                  </Tooltip>
                </>
              )}

              {isNew && onCancel && (
                <Tooltip title={t('actions.cancel')}>
                  <IconButton
                    size="small"
                    onClick={onCancel}
                    sx={{
                      width: { xs: 40, sm: 40 },
                      height: { xs: 40, sm: 40 },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          {/* Badges when not focused - desktop only (mobile badges shown inline above) */}
          {!isFocused && (
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
              {hasConditions && (
                <Tooltip title={t('conditions.hasConditions')}>
                  <Chip
                    icon={<AccountTreeIcon sx={{ fontSize: 14 }} />}
                    label={localConditions.filter(c => c.requiredOptionId > 0).length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      bgcolor: alpha('#2BC4AD', 0.1),
                      '& .MuiChip-icon': { color: 'primary.main' },
                    }}
                  />
                </Tooltip>
              )}
              {question.isRequired && !isSection && (
                <Typography variant="caption" color="error" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  *
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Question/Section Title - Always editable when focused */}
        {isFocused ? (
          <TextField
            inputRef={textInputRef}
            fullWidth
            multiline
            placeholder={isSection ? t('questionTypes.SECTION.placeholder') : t('question.textPlaceholder')}
            value={localText}
            onChange={(e) => handleTextChange(e.target.value)}
            onBlur={handleTextBlur}
            variant="standard"
            sx={{
              mb: isSection ? { xs: 0.75, sm: 1 } : { xs: 1.5, sm: 2 },
              '& .MuiInput-root': {
                fontSize: isSection ? { xs: '1.1rem', sm: '1.4rem' } : { xs: '1rem', sm: '1.1rem' },
                fontWeight: isSection ? 600 : 500,
              },
              '& .MuiInput-root:before': {
                borderBottom: '2px solid',
                borderColor: 'divider',
              },
              '& .MuiInput-root:hover:before': {
                borderColor: 'primary.main',
              },
            }}
          />
        ) : (
          <Typography
            variant={isSection ? 'h6' : 'body1'}
            sx={{
              fontWeight: isSection ? 600 : 500,
              fontSize: isSection ? { xs: '1.05rem', sm: '1.25rem' } : { xs: '0.95rem', sm: '1rem' },
              mb: { xs: 0.75, sm: 1 },
              color: localText ? 'text.primary' : 'text.secondary',
              minHeight: { xs: 20, sm: 24 },
            }}
          >
            {localText || (isSection ? t('questionTypes.SECTION.placeholder') : t('question.textPlaceholder'))}
          </Typography>
        )}

        {/* Section Description Field with Rich Text */}
        {isSection && (
          isFocused ? (
            <Box sx={{ mb: 1 }}>
              <RichTextEditor
                value={localDescription}
                onChange={setLocalDescription}
                onBlur={handleDescriptionBlur}
                placeholder={t('categories.dialog.descriptionPlaceholder')}
                minHeight={80}
              />
            </Box>
          ) : localDescription ? (
            <Box
              sx={{
                mb: 1,
                fontSize: '0.95rem',
                lineHeight: 1.6,
                color: 'text.secondary',
                '& ul': { pl: 3, my: 0.5, listStyleType: 'disc' },
                '& ol': { pl: 3, my: 0.5, listStyleType: 'decimal' },
                '& li': { mb: 0.25, display: 'list-item' },
                '& a': {
                  color: 'primary.main',
                  textDecoration: 'underline',
                  pointerEvents: 'none',  // Disable link clicks in collapsed view
                  cursor: 'default',
                },
              }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(localDescription) }}
            />
          ) : null
        )}

        {/* Options Editor - Inline like Google Forms (not for sections) */}
        {!isSection && supportsOptions && (
          <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
            {localOptions.map((option, index) => (
              <Box
                key={option.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.75, sm: 1 },
                  mb: { xs: 0.75, sm: 1 },
                }}
              >
                {/* Option indicator */}
                <Box
                  sx={{
                    width: { xs: 16, sm: 18 },
                    height: { xs: 16, sm: 18 },
                    borderRadius: typeCode === 'MULTIPLE_CHOICE' ? 0.5 : '50%',
                    border: 2,
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                />

                {isFocused ? (
                  <>
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      placeholder={`Option ${index + 1}`}
                      value={option.label}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      onBlur={handleOptionBlur}
                      autoFocus={newOptionIndex === index}
                      disabled={['SCALE5', 'TRUE_FALSE'].includes(typeCode)}
                      onFocus={() => {
                        // Clear the auto-focus flag after focusing
                        if (newOptionIndex === index) {
                          setNewOptionIndex(null);
                        }
                      }}
                      sx={{
                        '& .MuiInput-root': { fontSize: { xs: '0.875rem', sm: '0.95rem' } },
                      }}
                    />
                    {/* Only show delete button for non-Scale5 and non-TRUE_FALSE questions */}
                    {!['SCALE5', 'TRUE_FALSE'].includes(typeCode) && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveOption(index)}
                        sx={{
                          opacity: 0.5,
                          '&:hover': { opacity: 1 },
                          width: { xs: 36, sm: 40 },
                          height: { xs: 36, sm: 40 },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                    {option.label || `Option ${index + 1}`}
                  </Typography>
                )}
              </Box>
            ))}

            {/* Add Option button - only for user-editable option types */}
            {isFocused && !['SCALE5', 'TRUE_FALSE'].includes(typeCode) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.75, sm: 1 },
                  mt: { xs: 0.75, sm: 1 },
                  cursor: 'pointer',
                  opacity: 0.6,
                  '&:hover': { opacity: 1 },
                  minHeight: { xs: 40, sm: 'auto' },
                }}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent blur from firing
                  handleAddOption();
                }}
              >
                <Box
                  sx={{
                    width: { xs: 16, sm: 18 },
                    height: { xs: 16, sm: 18 },
                    borderRadius: typeCode === 'MULTIPLE_CHOICE' ? 0.5 : '50%',
                    border: 2,
                    borderStyle: 'dashed',
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                  {t('question.addOption')}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Scale preview */}
        {typeCode === 'SCALE5' && (
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 2 },
            mt: { xs: 1.5, sm: 2 },
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              {localStartLabel || t('scale.labels.lowDefault')}
            </Typography>
            <Box sx={{ display: 'flex', gap: { xs: 0.75, sm: 1 } }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Box
                  key={n}
                  sx={{
                    width: { xs: 28, sm: 24 },
                    height: { xs: 28, sm: 24 },
                    borderRadius: '50%',
                    border: 2,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>{n}</Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              {localEndLabel || t('scale.labels.highDefault')}
            </Typography>
          </Box>
        )}

        {/* True/False preview */}
        {typeCode === 'TRUE_FALSE' && (
          <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, mt: { xs: 1.5, sm: 2 } }}>
            {[localTrueLabel || 'Yes', localFalseLabel || 'No'].map((label) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
                <Box
                  sx={{
                    width: { xs: 16, sm: 18 },
                    height: { xs: 16, sm: 18 },
                    borderRadius: '50%',
                    border: 2,
                    borderColor: 'divider',
                  }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>{label}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Open Text preview */}
        {typeCode === 'OPEN_TEXT' && !isFocused && (
          <Box
            sx={{
              mt: { xs: 1.5, sm: 2 },
              p: { xs: 1.25, sm: 1.5 },
              bgcolor: alpha('#000', 0.02),
              borderRadius: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
              {localPlaceholder || t('questionTypes.OPEN_TEXT.placeholder')}
            </Typography>
          </Box>
        )}

        {/* Type-Specific Settings - Collapsible Section */}
        {isFocused && hasTypeSettings && (
          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            <Box
              onClick={() => setShowTypeSettings(!showTypeSettings)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.75, sm: 1 },
                cursor: 'pointer',
                py: { xs: 0.75, sm: 1 },
                '&:hover': { bgcolor: alpha('#000', 0.02) },
                borderRadius: 1,
                px: { xs: 0.75, sm: 1 },
                mx: { xs: -0.75, sm: -1 },
                minHeight: { xs: 44, sm: 'auto' },
              }}
            >
              <SettingsIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 500, flex: 1, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                {t('question.typeSettings.title')}
              </Typography>
              {showTypeSettings ? <ExpandLessIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> : <ExpandMoreIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
            </Box>

            <Collapse in={showTypeSettings}>
              <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Open Text Settings */}
                {typeCode === 'OPEN_TEXT' && (
                  <>
                    <TextField
                      label={t('openText.placeholder.label')}
                      fullWidth
                      size="small"
                      value={localPlaceholder}
                      onChange={(e) => setLocalPlaceholder(e.target.value)}
                      onBlur={handlePlaceholderBlur}
                      placeholder={t('openText.placeholder.placeholder')}
                      helperText={t('openText.placeholder.hint')}
                    />
                    <TextField
                      label={t('openText.maxLength.label')}
                      type="number"
                      fullWidth
                      size="small"
                      slotProps={{ htmlInput: { min: 1 } }}
                      value={localMaxLength || ''}
                      onChange={(e) => setLocalMaxLength(e.target.value ? Number(e.target.value) : undefined)}
                      onBlur={handleMaxLengthBlur}
                      placeholder={t('openText.maxLength.placeholder')}
                      helperText={t('openText.maxLength.hint')}
                    />
                  </>
                )}

                {/* Scale Settings */}
                {typeCode === 'SCALE5' && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                      {t('scale.labels.title')}
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                      <TextField
                        label={t('scale.labels.low')}
                        fullWidth
                        size="small"
                        value={localStartLabel}
                        onChange={(e) => setLocalStartLabel(e.target.value)}
                        onBlur={handleScaleLabelsBlur}
                        placeholder={t('scale.labels.lowPlaceholder')}
                      />
                      <TextField
                        label={t('scale.labels.high')}
                        fullWidth
                        size="small"
                        value={localEndLabel}
                        onChange={(e) => setLocalEndLabel(e.target.value)}
                        onBlur={handleScaleLabelsBlur}
                        placeholder={t('scale.labels.highPlaceholder')}
                      />
                    </Box>
                  </Box>
                )}

                {/* True/False Settings */}
                {typeCode === 'TRUE_FALSE' && (
                  <>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                        {t('trueFalse.labels.title')}
                      </Typography>
                      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                        <TextField
                          label={t('trueFalse.labels.affirmative')}
                          fullWidth
                          size="small"
                          value={localTrueLabel}
                          onChange={(e) => setLocalTrueLabel(e.target.value)}
                          onBlur={handleTrueFalseLabelsBlur}
                          placeholder={t('trueFalse.labels.affirmativePlaceholder')}
                        />
                        <TextField
                          label={t('trueFalse.labels.negative')}
                          fullWidth
                          size="small"
                          value={localFalseLabel}
                          onChange={(e) => setLocalFalseLabel(e.target.value)}
                          onBlur={handleTrueFalseLabelsBlur}
                          placeholder={t('trueFalse.labels.negativePlaceholder')}
                        />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        {t('trueFalse.presets.title')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {TRUE_FALSE_PRESETS.map((preset, index) => {
                          const isSelected = localTrueLabel === preset.trueLabel && localFalseLabel === preset.falseLabel;
                          return (
                            <Button
                              key={index}
                              variant="outlined"
                              size="small"
                              onClick={() => handleTrueFalseLabelsChange(preset.trueLabel, preset.falseLabel)}
                              sx={{
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                bgcolor: isSelected ? alpha('#2BC4AD', 0.1) : 'transparent',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  bgcolor: alpha('#2BC4AD', 0.05),
                                },
                              }}
                            >
                              {preset.trueLabel} / {preset.falseLabel}
                            </Button>
                          );
                        })}
                      </Box>
                    </Box>
                  </>
                )}

                {/* Selection Limits for Multiple/Ranked Choice */}
                {supportsLimits && (
                  <SelectionLimitsEditor
                    minSelections={localMinSelections}
                    maxSelections={localMaxSelections}
                    maxOptions={localOptions.length}
                    onMinChange={handleMinSelectionsChange}
                    onMaxChange={handleMaxSelectionsChange}
                    title={typeCode === 'RANKED_CHOICE' ? t('rankedChoice.requirements.title') : undefined}
                    minLabel={typeCode === 'RANKED_CHOICE' ? t('rankedChoice.requirements.minLabel') : undefined}
                    maxLabel={typeCode === 'RANKED_CHOICE' ? t('rankedChoice.requirements.maxLabel') : undefined}
                    helperText={t('selectionLimits.hint')}
                    minMin={typeCode === 'RANKED_CHOICE' ? 1 : 0}
                  />
                )}

                {/* Other Option Toggle for Choice types */}
                {supportsOther && (
                  <OtherOptionToggle
                    allowOtherText={localAllowOther}
                    otherTextMode={localOtherMode}
                    onAllowOtherChange={handleAllowOtherChange}
                    onModeChange={handleOtherModeChange}
                    description={typeCode === 'RANKED_CHOICE' ? t('rankedChoice.otherDescription') : undefined}
                  />
                )}

                {/* Conditional Display Logic */}
                <Divider sx={{ my: 1 }} />
                <ConditionBuilder
                  question={question}
                  allQuestions={allQuestions}
                  categories={categories}
                  conditions={localConditions}
                  onChange={handleConditionsChange}
                />
              </Box>
            </Collapse>
          </Box>
        )}
      </CardContent>
    </Card>
  );
});

export default InlineQuestionEditor;
